import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

import {
  ActivityProgressAnalytics,
  CourseProgressAnalytics,
  ModuleProgressAnalytics,
  ObjectiveProgressAnalytics,
} from "@axio/graphql/generated/graphql";
import { formatPercentage, getStatusColor, getSentimentColor } from "../../../../utils/analyticsUtils";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import StatBadge from "./StatBadge";
import ProgressBar from "./ProgressBar";

interface DrillDownPanelProps {
  label: string;
  item: CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics;
  type: "course" | "module" | "activity" | "objective";
  children: React.ReactNode;
  defaultOpen?: boolean;
  level?: number;
  inactive?: boolean;
}

const DrillDownPanel: React.FC<DrillDownPanelProps> = ({
  label,
  item,
  type,
  children,
  defaultOpen = false,
  level = 0,
  inactive = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const indentStyle = { marginLeft: level * 16 };
  const bgColor = level === 0 ? "#FFFFFF" : level === 1 ? "#F9FAFB" : level === 2 ? "#F3F4F6" : "#E5E7EB";

  const getBadge = () => {
    if (type === "objective" && item.status) {
      return (
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(item.status) + "20", borderColor: getStatusColor(item.status) },
          ]}>
          <CustomText
            text={
              item.status === "inProgress" ? "In Progress" : item.status === "completed" ? "Completed" : "Not Started"
            }
            size="xs"
            style={{ color: getStatusColor(item.status) }}
            useTranslationText={false}
          />
        </View>
      );
    } else if (item.sentiment) {
      return (
        <View
          style={[
            styles.statusBadge,
            {
              backgroundColor: getSentimentColor(item.sentiment) + "20",
              borderColor: getSentimentColor(item.sentiment),
            },
          ]}>
          <CustomText
            text={item.sentiment}
            size="xs"
            style={{ color: getSentimentColor(item.sentiment), textTransform: "capitalize" }}
            useTranslationText={false}
          />
        </View>
      );
    }
    return null;
  };

  const completionPercentage = item.completion_percentage
    ? item.completion_percentage > 1
      ? item.completion_percentage
      : item.completion_percentage * 100
    : 0;

  return (
    <View style={[styles.drillDownPanel, indentStyle, { backgroundColor: bgColor, opacity: inactive ? 0.6 : 1 }]}>
      <TouchableOpacity style={styles.panelHeader} onPress={() => !inactive && setOpen(!open)} disabled={inactive}>
        <View style={styles.panelHeaderLeft}>
          {open ? (
            <CaretDown size={16} color="#6B7280" style={styles.expandIcon} />
          ) : (
            <CaretRight size={16} color="#6B7280" style={styles.expandIcon} />
          )}
          <CustomText text={label} weight="bold" useTranslationText={false} />
          {getBadge()}
        </View>
      </TouchableOpacity>

      {type !== "objective" && completionPercentage > 0 && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <CustomText text="Completion" size="xs" />
            <CustomText text={formatPercentage(completionPercentage)} size="xs" useTranslationText={false} />
          </View>
          <ProgressBar percentage={completionPercentage} />
        </View>
      )}

      <View style={styles.metricsRow}>
        <StatBadge label="Success" value={item.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={item.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={item.questions_asked || 0} color="#3B82F6" />
        <StatBadge label="Interactions" value={item.active_interaction_count || 0} color="#8B5CF6" />
        <StatBadge label="Distractions" value={item.distractions || 0} color="#F59E0B" />
        <StatBadge label="Time" value={formatDurationFromSeconds(item.total_time_taken || 0)} color="#6B7280" />
      </View>

      {open && <View style={styles.panelContent}>{children}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  drillDownPanel: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  panelHeader: {
    marginBottom: 8,
  },
  panelHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  expandIcon: {
    marginRight: 8,
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    marginLeft: 8,
  },
  progressSection: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  panelContent: {
    marginTop: 4,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
});

export default DrillDownPanel;
